import { useMemo } from "react";
import { Cell, Pie, PieChart } from "recharts";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  ChartContainer,
  ChartConfig,
  ChartTooltip,
  ChartTooltipContent,
} from "@/components/ui/chart";
import { buildSpendingByCategory } from "@/lib/analytics";
import { Transaction } from "@/lib/types";
import { PieChart as PieChartIcon } from "lucide-react";
import { useCurrency } from "@/hooks/useCurrency";
// [T149-PR1] Adopt native Card structure + chart cell wrapper.
import { RechartsCell } from "@/components/dashboard/widgets/primitives/RechartsCell";

interface SpendingByCategoryChartProps {
  transactions: Transaction[];
}

export function SpendingByCategoryChart({
  transactions,
}: SpendingByCategoryChartProps) {
  const { fmt } = useCurrency();
  const data = useMemo(() => buildSpendingByCategory(transactions, new Date(), 6), [transactions]);

  const chartConfig = useMemo<ChartConfig>(() => {
    return data.reduce((config, item) => {
      config[item.category] = {
        label: item.category,
        color: item.color,
      };
      return config;
    }, {} as ChartConfig);
  }, [data]);

  return (
    <Card
      data-section="spending-by-category"
      data-testid="home-card-chart-spending"
      className="h-full transition-all duration-200 hover:shadow-md hover:border-primary/20 min-w-0 glass-panel"
    >
      <CardHeader className="flex flex-row items-center justify-between pb-4">
        <div className="flex items-center gap-3 min-w-0">
          <div className="p-2 bg-primary/10 rounded-full shrink-0">
            <PieChartIcon className="h-5 w-5 text-primary" />
          </div>
          <div className="min-w-0">
            <CardTitle className="truncate">Spending by Category</CardTitle>
            <p className="text-sm text-muted-foreground truncate">Current month expense distribution</p>
          </div>
        </div>
      </CardHeader>
      <CardContent className="overflow-y-auto px-2 sm:px-6">
      {data.length === 0 ? (
        <p className="text-sm text-muted-foreground">No expenses yet this month. Create expenses to view category breakdown.</p>
      ) : (
        <div className="grid gap-4 md:gap-6 lg:grid-cols-2 overflow-hidden min-w-0">
          <RechartsCell minHeight={220} className="mx-auto">
            <ChartContainer config={chartConfig} className="mx-auto h-[220px] md:h-[280px] w-full min-w-0 overflow-hidden">
              <PieChart>
                <ChartTooltip
                  content={<ChartTooltipContent nameKey="category" hideLabel />}
                />
                <Pie
                  data={data}
                  dataKey="amount"
                  nameKey="category"
                  innerRadius="35%"
                  outerRadius="65%"
                  paddingAngle={3}
                  strokeWidth={4}
                >
                  {data.map((item) => (
                    <Cell key={item.category} fill={item.color} />
                  ))}
                </Pie>
              </PieChart>
            </ChartContainer>
          </RechartsCell>

          <div className="space-y-3 min-w-0 overflow-hidden">
            {data.map((item) => (
              <div key={item.category} className="flex items-center justify-between gap-2 min-w-0">
                <div className="flex items-center gap-2 min-w-0 flex-1">
                  <span
                    className="h-2.5 w-2.5 rounded-full shrink-0"
                    style={{ backgroundColor: item.color }}
                  />
                  <span className="text-sm text-foreground truncate">{item.category}</span>
                </div>
                <div className="text-sm text-muted-foreground font-mono whitespace-nowrap shrink-0">
                  {fmt(item.amount)}
                  <span className="ml-1 text-xs">{item.percentage}%</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
      </CardContent>
    </Card>
  );
}
